import { Children, cloneElement, isValidElement, useEffect, useState, type ReactNode } from 'react';

import _ from 'lodash';

import { type TermType } from '@/src/Types/Shared/Taxonomy';

import { NestedListIcon, SearchLightIcon } from '../Icons';

type FilterType = {
	terms: TermType[];
	searchLabel: string;
	selectLabel: string;
	noResultLabel: string;
	value: string;
	selectClass?: string;
	actifClass?: string;
	className: string;
	activeIndex?: number;
	index?: number;
	setValue: (e: string) => void;
	setActiveIndex?: (e: number | undefined) => void;
};

export default function Filter(props: FilterType) {
	const {
		terms,
		searchLabel,
		selectLabel,
		noResultLabel,
		setValue,
		value,
		selectClass = '',
		className,
		activeIndex,
		setActiveIndex,
		index,
	} = props;

	const open = activeIndex === index;
	const [query, setQuery] = useState<string | null>(null);
	const [filteredTerms, setFilteredTerms] = useState<TermType[]>([]);

	const filter = (options: TermType[]) => {
		const result = query
			? options.filter((option) => option.name.toLowerCase().indexOf(query.toLowerCase()) > -1)
			: options;

		function buildMenuTreev2(items: TermType[], parentId?: number): TermType[] {
			const tree: TermType[] = [];

			for (const item of items) {
				if (item.parent === parentId) {
					const treeItem: TermType = {
						...item,
						children: buildMenuTreev2(items, item.term_id),
					};
					tree.push(treeItem);
				}
			}

			return tree;
		}

		function getMissingParent(elements: TermType[]) {
			elements.forEach((element) => {
				const resultParent = _.find(elements, { term_id: element.parent });
				if (!resultParent && element.parent != 0) {
					const newParent = _.find(terms, { term_id: element.parent });
					if (newParent) elements.push(newParent);
					if (newParent?.parent != 0) {
						getMissingParent(elements);
					}
				}
			});
			return elements;
		}

		const elements = buildMenuTreev2(getMissingParent(result), 0);

		return elements;
	};

	useEffect(() => {
		setFilteredTerms(filter(terms));
	}, [query]);

	return (
		<div className={className}>
			<div className={`relative tablet:min-w-[200px]`}>
				<button
					role='combobox'
					id='combo-list1'
					aria-controls='combo-list1-content'
					aria-expanded={open}
					aria-label={value ? terms.find((term) => term.slug === value)?.name : selectLabel}
					onClick={() => setActiveIndex && setActiveIndex(open ? undefined : index)}
					className={`${selectClass} flex w-full items-center justify-between gap-3 px-4 py-3`}
				>
					{value ? terms.find((term) => term.slug === value)?.name : selectLabel}
					<svg
						width='14'
						height='9'
						viewBox='0 0 14 9'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
						aria-hidden='true'
						className={`transition-all ${open ? 'rotate-180' : 'rotate-0'}`}
					>
						<path
							fillRule='evenodd'
							clipRule='evenodd'
							d='M7.00005 8.91421L0.292941 2.20711L1.70715 0.792894L7.00005 6.08579L12.2929 0.792895L13.7072 2.20711L7.00005 8.91421Z'
							fill='currentColor'
						/>
					</svg>
				</button>
				{open && (
					<div
						className={`${selectClass} absolute left-0 z-20 mt-6 max-h-[300px] w-full overflow-y-auto px-4 py-3 tablet:min-w-[200px]`}
					>
						{terms.length > 0 ? (
							<>
								<div className='flex items-center gap-1'>
									<SearchLightIcon />{' '}
									<input
										className='w-full border-none p-1'
										type='text'
										onChange={(e) => setQuery(e.target.value)}
										placeholder={searchLabel}
									/>
								</div>

								<div className='mt-5'>
									{filteredTerms.length > 0 ? (
										<Items array={filteredTerms} setValue={setValue} setActiveIndex={setActiveIndex} />
									) : (
										<>{noResultLabel}</>
									)}
								</div>
							</>
						) : (
							<>{noResultLabel}</>
						)}
					</div>
				)}
			</div>
		</div>
	);
}
Filter.displayName = 'Filter';

const Items = ({
	array,
	setValue,
	setActiveIndex,
	sub,
}: {
	array: TermType[];
	setValue: (e: string) => void;
	setActiveIndex?: (e: number | undefined) => void;
	sub?: number;
}) => {
	return (
		<>
			<ul className={`flex flex-col gap-1 ${sub && sub > 1 && 'ml-4'}`}>
				{array.map((term) => (
					<li key={term.term_id}>
						<div className='flex gap-1'>
							{sub && sub > 0 && <NestedListIcon />}
							<button
								className='w-full text-left leading-4 transition-all hover:opacity-50'
								onClick={() => {
									setValue(term.slug);
									setActiveIndex && setActiveIndex(undefined);
								}}
							>
								{term.name}
							</button>
						</div>
						{term.children && (
							<div className='mt-2'>
								<Items
									sub={sub ? sub + 1 : 1}
									array={term.children}
									setValue={setValue}
									setActiveIndex={setActiveIndex}
								/>
							</div>
						)}
					</li>
				))}
			</ul>
		</>
	);
};

const getComponentDisplayName = (element: React.ReactElement<any>) => {
	const node = element as React.ReactElement<React.ComponentType<any>>;
	const type = (node as unknown as React.ReactElement<React.FunctionComponent>).type;
	const displayName =
		typeof type === 'function'
			? (type as React.FunctionComponent).displayName ||
				(type as React.FunctionComponent).name ||
				'Unknown'
			: type;
	return displayName;
};

export type EnrichedChildren = {
	onChange(): void;
	selectedValue: string;
	name: string;
	children?: React.ReactNode;
};

export const FilterParent = ({ children }: { children: ReactNode }) => {
	const [activeIndex, setActiveIndex] = useState(undefined);
	const newChildren = Children.map(children, (child, index) => {
		if (!isValidElement<EnrichedChildren>(child)) {
			return child;
		}

		if (
			child &&
			isValidElement(child) &&
			typeof child.type === 'function' &&
			getComponentDisplayName(child) === 'Filter'
		) {
			return cloneElement(child as React.ReactElement, {
				setActiveIndex,
				activeIndex,
				index,
			});
		} else {
			return child;
		}
	});

	return <>{newChildren}</>;
};
